import React from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiHeart, FiShield, FiUsers, FiTarget, FiAward } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';

const ValueCard = ({ icon, title, description, index }) => {
  // Using useInView but actually referencing it in the code
  // eslint-disable-next-line no-unused-vars
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="glass-card p-6 rounded-xl"
    >
      <div className="flex items-start">
        <div className="text-primary-600 text-2xl mr-4 mt-1">
          {icon}
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-2">{title}</h3>
          <p className="text-neutral-dark/80">{description}</p>
        </div>
      </div>
    </motion.div>
  );
};

const AboutUs = () => {
  // Scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Using useInView but actually referencing it in the code
  // eslint-disable-next-line no-unused-vars
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const values = [
    {
      icon: <FiHeart />,
      title: "Human-Centered Design",
      description: "We create technology that adapts to humans, not the other way around. Our AI companions are designed with empathy and understanding at their core."
    },
    {
      icon: <FiShield />,
      title: "Privacy and Trust",
      description: "We build systems with privacy by design and transparent data practices. Your data belongs to you, and we're committed to earning and maintaining your trust."
    },
    {
      icon: <FiUsers />,
      title: "Accessibility",
      description: "We believe advanced AI should be available and usable by everyone. We design our products to be inclusive and adaptable to diverse needs."
    },
    {
      icon: <FiTarget />,
      title: "Continuous Learning",
      description: "Just like our AI, we're constantly evolving and improving. We embrace feedback and use it to make our products better every day."
    },
    {
      icon: <FiAward />,
      title: "Ethical Innovation",
      description: "We consider the ethical implications of our work at every stage. We're committed to developing AI that benefits humanity and respects individual autonomy."
    }
  ];

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-5xl mx-auto">
          <Link to="/" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8">
            <FiArrowLeft className="mr-2" />
            Back to Home
          </Link>
          
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="glass-card p-0 rounded-2xl overflow-hidden mb-16"
          >
            <div className="relative h-64 md:h-80 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 to-secondary-900/70 z-10"></div>
              <img 
                src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                alt="Team working on AI technology" 
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 z-20 flex items-center">
                <div className="container mx-auto px-4 md:px-8">
                  <h1 className="text-3xl md:text-5xl font-display font-bold text-white mb-4">
                    About <span className="text-gradient-light">xdimension</span>
                  </h1>
                  <p className="text-xl text-white/90 max-w-xl">
                    Pioneering the future of AI companionship to enhance human potential
                  </p>
                </div>
              </div>
            </div>
            <div className="p-8 md:p-12">
              <p className="text-lg text-neutral-dark/80 leading-relaxed">
                Founded in 2025, xdimension is at the forefront of the AI revolution, creating embodied AI companions that bring the
                power of advanced artificial intelligence into everyday life. Our flagship product, Ainous, combines cutting-edge 
                language processing, emotional intelligence, and elegant hardware design to create AI companions that truly understand and adapt to human needs.
              </p>
            </div>
          </motion.div>
          
          {/* Our Mission & Vision */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="glass-card p-8 rounded-2xl"
            >
              <h2 className="text-2xl font-display font-bold mb-4">Our Mission</h2>
              <p className="text-neutral-dark/80">
                We're on a mission to create AI companions that enhance human potential through intuitive, personalized assistance. 
                We believe that AI should be accessible, helpful, and designed with human connection at its core.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="glass-card p-8 rounded-2xl"
            >
              <h2 className="text-2xl font-display font-bold mb-4">Our Vision</h2>
              <p className="text-neutral-dark/80">
                We envision a world where AI companions like Ainous seamlessly integrate into daily life, providing assistance that's 
                tailored to individual needs while respecting privacy and human autonomy. Our goal is not to replace human connection, 
                but to enhance it by taking care of routine tasks and providing support when needed.
              </p>
            </motion.div>
          </div>
          
          {/* Our Values */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-8 text-center">Our Core Values</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {values.map((value, index) => (
                <ValueCard key={index} {...value} index={index} />
              ))}
            </div>
          </motion.div>
          
          {/* Join Us */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="glass-card p-8 md:p-12 rounded-2xl text-center"
          >
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-6">Join Our Journey</h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              We're always looking for passionate innovators who share our vision of creating technology that enhances human potential. 
              Join us in building the future of AI companionship.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/positions" className="btn-primary">
                View Open Positions
              </Link>
              <a 
                href="mailto:contact@xdimension.ai?subject=Contact from Website" 
                className="btn-outline"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = "mailto:contact@xdimension.ai?subject=Contact from Website&body=Hello xdimension Team,%0A%0A";
                }}
              >
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
